import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { UserRole } from '../models/user.model';

export const roleGuard = (allowedRoles: UserRole[]): CanActivateFn => {
  return () => {
    const auth = inject(AuthService);
    const router = inject(Router);

    if (!auth.isAuthenticated()) {
      return router.createUrlTree(['/login']);
    }

    const activeRole = auth.activeRole();

    // Admin with no role switch (or explicitly on 'admin') bypasses all restrictions
    if (auth.isAdmin() && (!activeRole || activeRole === 'admin')) {
      return true;
    }

    // Admin with an active role switch — enforce the switched role
    if (auth.isAdmin() && activeRole) {
      if (allowedRoles.includes(activeRole)) {
        return true;
      }
      // Redirect based on the switched role
      if (activeRole === 'supplier') {
        return router.createUrlTree(['/portal/supplier']);
      }
      if (activeRole === 'inspector') {
        return router.createUrlTree(['/portal/inspector']);
      }
      if (activeRole === 'external-inspection-coordinator') {
        return router.createUrlTree(['/portal/coordinator']);
      }
      if (activeRole === 'external-supplier-coordinator') {
        return router.createUrlTree(['/portal/supplier-coordinator']);
      }
      return router.createUrlTree(['/']);
    }

    // Non-admin: check if their active role is in the allowed list
    if (auth.hasAnyRole(allowedRoles)) {
      return true;
    }

    // Redirect external-only users to their portal
    if (auth.isExternalOnly()) {
      if (activeRole === 'supplier') {
        return router.createUrlTree(['/portal/supplier']);
      }
      if (activeRole === 'inspector') {
        return router.createUrlTree(['/portal/inspector']);
      }
      if (activeRole === 'external-inspection-coordinator') {
        return router.createUrlTree(['/portal/coordinator']);
      }
      if (activeRole === 'external-supplier-coordinator') {
        return router.createUrlTree(['/portal/supplier-coordinator']);
      }
    }
    return router.createUrlTree(['/']);
  };
};
